import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import './SiteSearchPage.css';

const SEARCH_ENTRIES = [
  {
    title: 'Products',
    path: '/products',
    category: 'Store',
    description: 'Expert Advisors, indicators, source code, set files, and product downloads.',
    keywords: 'ea expert advisor indicator source code set file products store download',
  },
  {
    title: 'VixAlgoBuilder',
    path: '/tester',
    category: 'Builder',
    description: 'Build, test, save, export, and replay trading strategies visually.',
    keywords: 'builder backtest strategy tester mq5 export chart rules indicators market',
  },
  {
    title: 'VixAI',
    path: '/tester?panel=ai',
    category: 'Builder',
    description: 'Use VixAI inside the Builder to draft or adjust strategy setups.',
    keywords: 'ai vixai strategy architect dsl custom visuals inputs panels guide',
  },
  {
    title: 'Builder Documentation',
    path: '/builder-docs',
    category: 'Guide',
    description: 'Learn the Builder workflow, modeling modes, export checks, and validation steps.',
    keywords: 'docs documentation guide walkthrough tutorial mt5 validation export parity',
  },
  {
    title: 'VixPremium',
    path: '/tester-premium',
    category: 'Subscription',
    description: 'Live cloud signals, MT5 copy trading, hosted EA imports, live account tracking, Builder export, and community access.',
    keywords: 'premium subscription vixpremium signals copier copy trading mt5 import ea mql5 live tracking export community ai saved strategies',
  },
  {
    title: 'Community',
    path: '/community',
    category: 'Community',
    description: 'Share strategy threads, files, reports, discussions, and public Builder snapshots.',
    keywords: 'community thread comment report file strategy public share download import',
  },
  {
    title: 'Dashboard',
    path: '/dashboard',
    category: 'Account',
    description: 'Manage saved strategies, purchases, licenses, billing, support, and settings.',
    keywords: 'dashboard account strategies purchases licenses billing support settings',
  },
  {
    title: 'Support',
    path: '/dashboard?tab=support',
    category: 'Account',
    description: 'Open support conversations and attach files when you need help.',
    keywords: 'support ticket chat help issue contact attachments',
  },
  {
    title: 'FAQ',
    path: '/faq',
    category: 'Help',
    description: 'Answers to common questions about licensing, downloads, payments, and setup.',
    keywords: 'faq questions help license download payment setup',
  },
  {
    title: 'Re-download',
    path: '/redownload',
    category: 'Store',
    description: 'Recover a previous purchase download using your order information.',
    keywords: 'redownload re-download order purchase file recover',
  },
  // Keywords carry the words people actually type when something is not working — "4014",
  // "not trading", "lot size" — so search lands them on the page that explains it rather than
  // returning nothing.
  {
    title: 'Signals Marketplace',
    path: '/signals',
    category: 'Signals',
    description: 'Browse strategies running live in the cloud, with verified track records you can follow.',
    keywords: 'signals marketplace browse follow copy trading verified track record leaderboard creators public',
  },
  {
    title: 'Cloud Signals',
    path: '/dashboard?tab=signals',
    category: 'Signals',
    description: 'Run a saved strategy on live market data in the cloud — no MetaTrader, no VPS — and get alerted when it trades.',
    keywords: 'cloud signals deploy live run strategy alerts notifications telegram email discord no vps metatrader virtual account paper follow',
  },
  {
    title: 'MT5 Signal Copier',
    path: '/dashboard?tab=signals',
    category: 'Signals',
    description: 'Mirror the signals you follow into your own MetaTrader 5 account, with lot sizing set per strategy.',
    keywords: 'copier ea mt5 metatrader copy trades mirror lot size volume minimum webrequest whitelist 4014 token not trading magic number symbol mapping suffix',
  },
  {
    title: 'Live Performance Tracking',
    path: '/dashboard?tab=live',
    category: 'Signals',
    description: 'Link a MetaTrader account so an exported EA reports its real results back to your dashboard.',
    keywords: 'live tracking myfxbook verified results mt5 account link vixlink real trades equity consistency broker statement',
  },
  {
    title: 'Alert Settings',
    path: '/dashboard?tab=signals',
    category: 'Signals',
    description: 'Set your Telegram, Discord, email and in-app alert channels once for every signal.',
    keywords: 'alerts notifications telegram discord webhook email in-app channels test settings not receiving',
  },
];

function getQuery(search) {
  return new URLSearchParams(search).get('q')?.trim() || '';
}

function SiteSearchPage() {
  const location = useLocation();
  const query = getQuery(location.search);
  const results = useMemo(() => {
    if (!query) {
      return SEARCH_ENTRIES;
    }
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return SEARCH_ENTRIES
      .map((entry) => {
        const haystack = `${entry.title} ${entry.category} ${entry.description} ${entry.keywords}`.toLowerCase();
        const score = terms.reduce((sum, term) => sum + (haystack.includes(term) ? 1 : 0), 0);
        return { ...entry, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((left, right) => right.score - left.score || left.title.localeCompare(right.title));
  }, [query]);

  const productSearchPath = query ? `/products?search=${encodeURIComponent(query)}` : '/products';
  const communitySearchPath = query ? `/community?search=${encodeURIComponent(query)}` : '/community';

  return (
    <HelmetProvider>
      <div className="site-search page-shell">
        <Helmet>
          <title>{query ? `Search: ${query}` : 'Search'} - Vix Algo Trading</title>
          <meta name="description" content="Search Vix Algo Trading products, Builder tools, documentation, community, support, and account areas." />
        </Helmet>

        <section className="site-search-hero">
          <span className="tag">Search</span>
          <h1 className="page-title">{query ? `Results for "${query}"` : 'Search the whole site'}</h1>
          <p className="page-lead">Find products, Builder tools, VixAI, documentation, community threads, support, downloads, and account pages.</p>
          <div className="site-search-shortcuts">
            <Link to={productSearchPath} className="ghost-cta">Search Products</Link>
            <Link to={communitySearchPath} className="ghost-cta">Search Community</Link>
            <Link to="/tester?panel=ai" className="ghost-cta">Open VixAI</Link>
          </div>
        </section>

        <section className="site-search-results">
          {results.length ? results.map((entry) => (
            <Link key={entry.path} to={entry.path} className="site-search-card section-card">
              <span>{entry.category}</span>
              <strong>{entry.title}</strong>
              <p>{entry.description}</p>
            </Link>
          )) : (
            <div className="section-card site-search-empty">
              <strong>No direct matches.</strong>
              <p>Try a shorter phrase, or search products/community directly.</p>
              <div className="site-search-shortcuts">
                <Link to={productSearchPath} className="primary-cta">Products</Link>
                <Link to={communitySearchPath} className="ghost-cta">Community</Link>
              </div>
            </div>
          )}
        </section>
      </div>
    </HelmetProvider>
  );
}

export default SiteSearchPage;
